/* istanbul ignore file */
/**
 * Created by rathompson on 9/13/21
 * File: IdentitiesTable
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { dispatch as globalDispatch, actions } from '@sparky/framework'
import PropTypes from 'prop-types'
import { useModal, Button, TruncateText } from '@panwds/react-ui'
import {
  ConnectedTable,
  TableProvider,
  ConnectedTableLayout,
  useTableInstance, ConnectedPaginator,
} from '@panwds/react-table'
import { LoadTreeData } from '../../../../../common/actions/GlobalActions'
import { useReactOidc } from '@axa-fr/react-oidc-context'
import {
  LABEL_HEADER_TYPE,
  LABEL_HEADER_DISPLAY_NAME,
  LABEL_HEADER_IDENTITY,
  LABEL_HEADER_APPLICATIONS,
  LABEL_HEADER_ROLE,
  LABEL_INHERITED,
  BUTTON_ADD,
  BUTTON_REMOVE,
  BUTTON_ASSIGN_ROLES,
  PLACEHOLDER_SEARCH,
  LABEL_HEADER_SUBTITLE,
  LABEL_IDENTITIES_SUBTENANT,
  MESSAGE_MULTI_IDENTITIES_REMOVED,
  MESSAGE_SINGLE_IDENTITY_REMOVED,
  LABEL_HEADER_SCOPE,
} from '../../../../constants/LabelConstants'
import TableHeader from '../../../../../common/components/table/TableHeader'
import ViewIdentityModal from '../../modal/ViewIdentityModal'
import TypeRenderer from './../cellRenderer/TypeRenderer'
import RoleRenderer from './../cellRenderer/RoleRenderer'
import IdentityAddressRenderer from './../cellRenderer/IdentityAddressRenderer'
import DisplayNameRenderer from './../cellRenderer/DisplayNameRenderer'
import ApplicationsRenderer from './../cellRenderer/ApplicationsRenderer'
import InheritedCellRenderer from './../cellRenderer/InheritedCellRenderer'
import AddIdentityModal from '../../modal/AddIdentityModal'
import IdentityModal from '../../modal/IdentityModal/IdentityModal'
import FiltersBar from './../FiltersBar'
import AssignRolesModal from '../../modal/AssignRolesModal/AssignRolesModal'
import RemoveIdentityModal from '../../modal/RemoveIdentityModal'

import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import { IDENTITY_TYPES } from '../../../../constants/IAMConstants'
import IAMService from '../../../../services/IAMService'
import { getVarValue } from '../../../../../common/utils/Sparky'
import { selectTreeNode } from '../../../../../common/reducers/actions/TreeReducer.actions'

import './../IdentitiesTable.scss'
import {
  FF_DISPLAY_SCOPE_COLUMN,
  FF_ENABLE_SERVICE_ACCOUNT,
  FF_ROLES_ENABLED,
} from '../../../../../common/utils/FeatureFlags'
import { prn } from '@sparky/framework'
import { ROOT_NODE_ID } from '../../../../../tenancy/constants/TenancyConstants'
import { useMessage } from '../../../../../common/providers/MessageProvider'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import GlobalContext from '../../../../../common/context/GlobalContext'
import ConnectedTableFilter from '../../../../../common/components/table/tableFilter/ConnectedTableFilter'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import { useLoadingContext } from '../../../../../common/context/LoadingContext'
import _ from 'lodash'
import ScopeRenderer from './../cellRenderer/ScopeRenderer'
import IAMAppName from '../../../../utils/IAMAppName'
import AppContext from '../../../../../common/context/AppContext'

const enableService = getVarValue(FF_ENABLE_SERVICE_ACCOUNT, false)
const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)
const displayScopeColumn = getVarValue(FF_DISPLAY_SCOPE_COLUMN, false)

const isChildOf = (tenant, selected) => {
  let found = false
  if (ARRAY.hasArray(tenant.children)) {
    for (const child of tenant.children) {
      if (child?.id === selected?.id) {
        return true
      }
      if (ARRAY.hasArray(child.children)) {
        found = isChildOf(child, selected)
        if (found) {
          return found
        }
      }
    }
  }
  return found
}

const selectLowerTSG = (tsgA, tsgB) => {
  if (!tsgA) {
    return tsgB
  }
  if (!tsgB) {
    return tsgA
  }
  if (isChildOf(tsgA, tsgB)) {
    return tsgB
  }
  if (isChildOf(tsgB, tsgA)) {
    return tsgA
  }
  return undefined
}

const getMembers = (accessPolicies = [], serviceAccounts = [], tsgMap) => {
  const policyMap = ARRAY.clean(accessPolicies).reduce((map, policy) => {
    const { id, inherited_from, principal, resource, principal_type, principal_display_name, source } = policy
    if (!map.hasOwnProperty(principal)) {
      map[principal] = {
        ids: [],
        identity_email: principal,
        display_name: principal_display_name,
        policies: [],
        roles: {},
        inherited_from: [],
        apps: [],
        identity_type: principal_type,
        sources: new Set()
      }
    }
    map[principal].ids.push(id)
    map[principal].inherited_from.push(inherited_from)
    map[principal].sources.add(source || 'IAM')
    let resourceData
    if (typeof resource === 'string') {
      resourceData = prn.parse(resource)
    }
    else if (OBJECT.isObject(resource)) {
      resourceData = resource
    }

    //const { tsg_id, app_id, region_id, instance_id, resource_scope } = resourceData
    const { app_id } = resourceData
    if (app_id) {
      map[principal].apps.push(app_id)
    }
    if (!map[principal].roles.hasOwnProperty(app_id)) {
      map[principal].roles[app_id] = []
    }

    policy.resource = resourceData
    const existingPolicy = map[principal].policies.find(item => item.resource.app_id === policy.resource.app_id && item.resource.resource_scope === policy.resource.resource_scope && item.role === policy.role)
    if (existingPolicy) {
      const bottomTSG = selectLowerTSG(tsgMap[existingPolicy.inherited_from], tsgMap[policy.inherited_from])
      if (bottomTSG && bottomTSG?.id !== existingPolicy.inherited_from) {
        map[principal].policies = map[principal].policies.filter(({ id }) => id !== existingPolicy.id)
        map[principal].policies.push(policy)
        map[principal].roles[app_id].push(policy)
      }
    }
    else {
      map[principal].policies.push(policy)
      map[principal].roles[app_id].push(policy)
    }
    map[principal].policies = map[principal].policies.sort((a, b) => {
      if (a.resource.app_id === b.resource.app_id) {
        return a.resource.resource_scope > b.resource.resource_scope ? 1 : -1
      }
      return a.resource.app_id > b.resource.app_id ? 1 : -1
    })
    map[principal].apps = map[principal].apps.sort()
    return map
  }, {})
  ARRAY.clean(serviceAccounts).forEach(({ client_id, id, inherited_from, name, tsg_id, contact_email, description, source }) => {
    if (policyMap[client_id]) {
      policyMap[client_id] = {
        ...policyMap[client_id],
        name,
        tsg_id,
        client_id,
        id,
        contact_email,
        description,
        inherited_from: [...new Set([...policyMap[client_id].inherited_from, inherited_from])],
        sources: new Set()
      }
    }
    else {
      policyMap[client_id] = {
        ids: [],
        identity_email: client_id,
        display_name: name,
        policies: [],
        roles: {},
        inherited_from: [inherited_from],
        apps: [],
        identity_type: IDENTITY_TYPES.SERVICE_ACCOUNT,
        name,
        tsg_id,
        client_id,
        id,
        contact_email,
        description,
        sources: new Set()
      }
    }
    policyMap[client_id].sources.add(source || 'IAM')
  })
  return Object.values(policyMap)
}

const getApps = (accessPolicies = [], getAppName) => {
  return Object.values(ARRAY.clean(accessPolicies).reduce((map, policy) => {

    const { resource } = policy

    let resourceData
    if (typeof resource === 'string') {
      resourceData = prn.parse(resource)
    }
    else if (OBJECT.isObject(resource)) {
      resourceData = resource
    }

    const { app_id } = resourceData
    if (app_id && !map.hasOwnProperty(app_id)) {
      map[app_id] = { id: app_id, value: getAppName(app_id) }
    }

    return map
  }, {}))
}

const getRoles = (accessPolicies = [], roleMap = {}, customRoleMap = {}) => {
  return Object.values(ARRAY.clean(accessPolicies).reduce((map, policy) => {
    const { role } = policy
    if (role && !map.hasOwnProperty(role)) {
      map[role] = { id: role, value: role }
    }
    if (roleMap[role]) {
      map[role].value = roleMap[role].label
    }
    if (customRoleMap[role]) {
      map[role].value = customRoleMap[role].name
    }
    return map
  }, {}))
}

const sortApps = (itemA, itemB, sortBy) => {
  const apps_a = itemA.apps.sort().join('')
  const apps_b = itemB.apps.sort().join('')
  return sortBy.desc ? apps_b.localeCompare(apps_a) : apps_a.localeCompare(apps_b)
}

const sortRoles = (itemA, itemB, sortBy, roleMap, customRoleMap) => {
  const roles_a = [...new Set(_.flatten(Object.values(itemA?.roles || {})).map(({ role }) => (roleMap[role]?.label || customRoleMap[role]?.name || '')))].sort().join('')
  const roles_b = [...new Set(_.flatten(Object.values(itemB?.roles || {})).map(({ role }) => (roleMap[role]?.label || customRoleMap[role]?.name || '')))].sort().join('')
  return sortBy.desc ? roles_b.localeCompare(roles_a) : roles_a.localeCompare(roles_b)
}

const sortInherited = (itemA, itemB, sortBy, tsgMap) => {
  const tsgs_a = [...new Set(itemA.inherited_from)].map(tsg_id => tsgMap[tsg_id]?.display_name || '').sort().join('')
  const tsgs_b = [...new Set(itemB.inherited_from)].map(tsg_id => tsgMap[tsg_id]?.display_name || '').sort().join('')
  return sortBy.desc ? tsgs_b.localeCompare(tsgs_a) : tsgs_a.localeCompare(tsgs_b)
}

const defaultSort = (itemA, itemB, sortBy) => {
  const stringA = itemA[sortBy?.id] || itemA?.identity_email
  const stringB = itemB[sortBy?.id] || itemB?.identity_email
  return sortBy.desc ? stringB.localeCompare(stringA) : stringA.localeCompare(stringB)
}

/***
 *
 * @returns {JSX.Element}
 * @constructor
 * @desc Displays all identities found for the currently selected tenant, has search filter and pagination controls
 */
const IdentitiesTable = ({ getData: _getData, canListPolicies }) => {

  const service = IAMService
  const { treeReducer = {}, iamReducer = {}, dispatch } = useContext(GlobalContext)
  const { globalTsgId, sgKeyBy, selectedTreeNode, flattenTree } = treeReducer
  const { accessPolicies, serviceAccounts, roleMap, customRoleMap } = iamReducer
  const { loading } = useLoadingContext()

  /**
   *     ========= Local State ===========
   */
  const { oidcUser } = useReactOidc()
  const [sortBy, setSortBy] = useState(null)
  const [data, setData] = useState([])
  const [member, setMember] = useState(null)
  const [resolvedData, setResolvedData] = useState([])
  const [filters, setFilters] = useState({})

  const { createBanner } = useMessage()

  const viewModal = useModal(ViewIdentityModal)
  const addModal = useModal(enableService ? IdentityModal : AddIdentityModal)
  const removeModal = useModal(RemoveIdentityModal)
  const assignRoles = useModal(AssignRolesModal)

  const { getAppName } = useContext(AppContext)

  /**
   *     ========= Callback =============
   */
  const getNextTSG = useCallback(() => {
    const roots = flattenTree?.filter(tenant => tenant.isRoot)
    const tenants = roots?.filter(tenant => tenant?.id !== '_superRoot' && tenant?.id !== selectedTreeNode?.id)
    if (selectedTreeNode?.parent_id !== '_superRoot') {
      return selectedTreeNode
    }
    if (selectedTreeNode?.id !== globalTsgId) {
      const globalTsg = flattenTree.find(tsg => tsg?.id === globalTsgId)
      if (isChildOf(selectedTreeNode, globalTsg)) {
        return tenants[0] || globalTsg
      }
      return globalTsg
    }
    return tenants[0]
  }, [flattenTree, globalTsgId, selectedTreeNode])

  useEffect(() => {
    if (rolesEnabled) {
      setData(getMembers(accessPolicies, serviceAccounts, sgKeyBy))
    }
  }, [accessPolicies, serviceAccounts, sgKeyBy])

  const apps = useMemo(() => getApps(accessPolicies, (value) => IAMAppName(value, getAppName)), [accessPolicies, getAppName])

  const roles = useMemo(() => getRoles(accessPolicies, roleMap, customRoleMap), [accessPolicies, roleMap, customRoleMap])

  const getData = useCallback(async (member) => {
    if (selectedTreeNode?.id && selectedTreeNode?.id !== '_superRoot') {
      try {
        setFilters({})
        if (rolesEnabled) {
          await _getData(selectedTreeNode?.id, dispatch)
        }
        else {
          const { data } = await service?.members.get(selectedTreeNode?.id) || {}
          setData(data)
        }
      }
      catch (e) {
        // TODO: Handle server errors
        // setError(e.message)
      }
      if (OBJECT.hasObject(member)) {
        setMember(member)
      }
      else {
        setMember(null)
      }
    }
  }, [_getData, dispatch, selectedTreeNode?.id, service?.members])

  const addIdentity = useCallback((tsg_id, { identity_type, identity_email }) => {
    return service?.members.post(tsg_id, { identity_type, identity_email })
  }, [service])

  const removeIdentity = useCallback(async (tsg_id, identities) => {
    let response
    let isSelfRemove
    for (const identity of identities) {
      if (!isSelfRemove) {
        if (rolesEnabled) {
          isSelfRemove = identity?.ids?.includes(oidcUser?.profile?.sub) || identity?.identity_email === oidcUser?.profile?.email
        }
        else {
          isSelfRemove = identity?.identity_id === oidcUser?.profile?.sub
        }
      }
      if (rolesEnabled) {
        for (const policy of identity.policies) {
          const { tsg_id } = policy.resource
          if (tsg_id !== selectedTreeNode?.id) {
            continue
          }
          if (policy.source) {
            continue
          }
          response = await service?.accessPolicies.delete(tsg_id, policy?.id)
        }
      }
      if (identity?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT && identity?.tsg_id === tsg_id) {
        response = await service?.serviceAccounts.delete(tsg_id, identity?.identity_id || identity?.id)
      }
      else if (!rolesEnabled) {
        response = await service?.members.delete(tsg_id, identity?.id)
      }
    }
    if (isSelfRemove) {
      const tsg = getNextTSG()
      if (!tsg || !tsg?.id) {
        await actions?.logout()
      }
      if (tsg?.id !== selectedTreeNode?.id) {
        await globalDispatch.switchTsg(tsg?.id, { forceReload: true })
        dispatch(selectTreeNode(tsg))
        LoadTreeData(dispatch)
      }
    }
    const message = identities.length > 1 ?
      StringTemplate(MESSAGE_MULTI_IDENTITIES_REMOVED, [identities?.length]) :
      StringTemplate(MESSAGE_SINGLE_IDENTITY_REMOVED, [identities[0]?.display_name || identities[0]?.identity_email])
    createBanner({ message, bannerProps: { appearance: 'success', type: 'page' } })
    return response
  }, [createBanner, dispatch, getNextTSG, oidcUser?.profile?.email, oidcUser?.profile?.sub, selectedTreeNode?.id, service?.accessPolicies, service?.members, service?.serviceAccounts])

  const onRowClick = useCallback(async (row, tenant) => {
    if (enableService) {
      // TODO: Eventually Remove Service Account Feature Flag
      addModal.openModal({ tenant, getData, member: row.original, isEdit: true, addIdentity, removeIdentity, length: resolvedData?.length })
    }
    else {
      viewModal.openModal({ tenant, getData, removeIdentity, ...row.original })
    }
  }, [addIdentity, addModal, getData, removeIdentity, viewModal, resolvedData])

  const scrollToRow = useCallback((row) => {
    if (!rolesEnabled) {
      const rowElement = document.querySelector(`[aria-rowindex="${row?.index}"]`)
      rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    }
  }, [])

  const updateCheckboxes = useCallback((rows) => {
    if (!selectedTreeNode) {
      return []
    }
    rows.forEach((item, index) => {
      if (!selectedTreeNode) return
      const tableContainer = document.getElementById('identities-container')
      const rowElement = tableContainer.querySelector(`[aria-rowindex="${index}"]`)
      if (rowElement) {
        const label = rowElement.querySelector('label')
        const isInheritedMember = !item.inherited_from.includes(selectedTreeNode?.id)
        const isOnlyMember = data?.length <= 1
        const isIDPMember = !item.sources.has('IAM')
        const disabled = isInheritedMember || isOnlyMember || isIDPMember
        rows[index].isInheritedMember = isInheritedMember
        rows[index].isOnlyMember = isOnlyMember
        rows[index].isIDPMember = isIDPMember
        const checkbox = rowElement.querySelector('input[type=checkbox]')
        if (checkbox) {
          checkbox.disabled = disabled
          checkbox.readOnly = disabled
          if (disabled) {
            label.classList.add('inherited-check')
            checkbox.classList.remove('tw-cursor-pointer')
            checkbox.classList.add('tw-cursor-not-allowed')
            checkbox.style.cursor = 'not-allowed'
            checkbox['aria-checked'] = false
            checkbox.checked = false
          }
          else {
            label.classList.remove('inherited-check')
            checkbox.classList.remove('tw-cursor-not-allowed')
            checkbox.classList.add('tw-cursor-pointer')
            checkbox.style.cursor = 'pointer'
          }
        }
      }
    })
    return rows
  }, [data, selectedTreeNode])

  const onSortChange = useCallback((sortBy) => {
    if (sortBy[0]) {
      setSortBy(sortBy[0])
    }
    else {
      setSortBy(null)
    }
  }, [])

  const shouldHighlightRow = useCallback(({ row }) => {
    if (!rolesEnabled) {
      const shouldHighlight = row?.original?.identity_id === member?.identity_id
      if (shouldHighlight) {
        scrollToRow(row)
      }
      return shouldHighlight
    }
    else {
      const shouldHighlight = row?.original?.ids?.some(id => member?.ids?.indexOf(id) >= 0)
      if (shouldHighlight) {
        scrollToRow(row)
      }
      return shouldHighlight
    }
  }, [member?.identity_id, member?.ids, scrollToRow])

  useEffect(() => {
    setResolvedData(data)
  }, [data])

  const columns = useMemo(() => {
    const columns = [
      {
        accessor: row => row?.identity_type,
        Header: LABEL_HEADER_TYPE,
        Cell: TypeRenderer,
        id: 'identity_type',
        sticky: true,
        stickyPosition: 'left',
        disableSortBy: true,
        disableResizing: true,
        width: 100
      },
      {
        accessor: row => row.display_name?.trim() || row?.identity_email,
        Header: LABEL_HEADER_DISPLAY_NAME,
        Cell({ row, value }) {
          return <DisplayNameRenderer{...{ row, value }} onClick={onRowClick.bind(this, row, selectedTreeNode)}/>
        },
        width: 232,
        id: 'display_name',
      },
      {
        accessor: row => row?.identity_email,
        Header: LABEL_HEADER_IDENTITY,
        Cell: IdentityAddressRenderer,
        width: 250,
        id: 'identity_email',
      },
      {
        accessor: row => row?.apps,
        Header: LABEL_HEADER_APPLICATIONS,
        Cell: ApplicationsRenderer,
        width: 232,
        id: 'applications',
      },
      {
        accessor: row => row?.roles,
        Header: LABEL_HEADER_ROLE,
        Cell: RoleRenderer,
        width: 190,
        id: 'role',
      },
      {
        accessor: row => row?.scopes,
        Header: LABEL_HEADER_SCOPE,
        Cell: ScopeRenderer,
        width: 190,
        id: 'scope'
      },
      {
        accessor: row => row?.inherited_from,
        Header: LABEL_INHERITED,
        Cell: (props) => <InheritedCellRenderer {...{ sgKeyBy, selectedTreeNode, ...props }}/>,
        width: 232,
        id: 'inherited_from',
      },
    ]
    if (!displayScopeColumn) {
      // Using filter to preserve column order
      return columns.filter(col => col.id !== 'scope')
    }
    return columns
  }, [onRowClick, selectedTreeNode, sgKeyBy])

  const filterRows = useCallback(rows => {
    let array = rows
    if (rolesEnabled) {
      if (filters.type) {
        switch (filters.type?.id) {
          case IDENTITY_TYPES.USER:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.USER)
            break
          case IDENTITY_TYPES.SERVICE_ACCOUNT:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT)
            break
          case IDENTITY_TYPES.GROUP:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.GROUP)
            break
          case 'none':
            array = array?.filter(member => !ARRAY.hasArray(member?.policies))
            break
          case 'all':
          default:
        }
      }
      if (filters?.app && filters?.app?.id) {
        array = array.filter(({ apps }) => apps?.includes(filters?.app?.id))
      }
      if (filters?.role && filters?.role?.id) {
        array = filters?.role?.id === 'all' ? array : array.filter(({ roles }) => {
          if (OBJECT.hasObject(roles)) {
            for (const [, roleArray] of Object.entries(roles)) {
              const hasRole = roleArray.some(({ role }) => role === filters.role.id)
              if (hasRole) {
                return true
              }
            }
          }
          return false
        })
      }
    }
    return array
  }, [filters])

  const rows = useMemo(() => {
    let array = filterRows(resolvedData)
    if (OBJECT.hasObject(sortBy)) {
      const sortFunction = (itemA, itemB) => {
        switch (sortBy.id) {
          case 'applications':
            return sortApps(itemA, itemB, sortBy)
          case 'inherited_from':
            return sortInherited(itemA, itemB, sortBy, sgKeyBy)
          case 'role':
            return sortRoles(itemA, itemB, sortBy, roleMap, customRoleMap)
          case 'identity_email':
          case 'display_name':
          case 'identity_type':
          default:
            return defaultSort(itemA, itemB, sortBy)
        }
      }
      array = array.sort(sortFunction)
    }
    const rows = array
    updateCheckboxes(rows)
    return rows
  }, [filterRows, resolvedData, sortBy, updateCheckboxes, roleMap, customRoleMap, sgKeyBy])

  const RightControls = () => {
    const useSelectedRowData = (tableInstance) => {
      const selectedRows = Object.keys(tableInstance.state.selectedRowIds)
      return selectedRows.map((index) => tableInstance.data[Number(index)])?.filter((item) => {
        if (!item || data?.length <= 1) {
          return false
        }
        if (rolesEnabled) {
          if (!item.sources.has('IAM')) return false
          return item.inherited_from.includes(selectedTreeNode?.id)
        }
        else {
          return item.inherited_from === selectedTreeNode?.id
        }
      })
    }
    const selected = useSelectedRowData(useTableInstance())
    const hasSelected = ARRAY.hasArray(Object.keys(selected))
    return (
      <>
        {rolesEnabled && (
          <Button
            addClassName={hasSelected ? '' : 'tw-bg-gray-300 tw-text-dark-bg'}
            appearance={hasSelected ? 'primary' : 'secondary'}
            disabled={!hasSelected || !Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID}
            onClick={() => assignRoles.openModal({ tenant: selectedTreeNode, selected, getData })}
          >{BUTTON_ASSIGN_ROLES}</Button>
        )}
        <div className="relative">
          <Button
            appearance='secondary'
            onClick={() => addModal.openModal({ tenant: selectedTreeNode, getData, addIdentity, removeIdentity })}
            disabled={!Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID}
          >{BUTTON_ADD}</Button>
        </div>
        <Button appearance={hasSelected ? 'secondary-destructive' : 'tertiary'}
          disabled={!hasSelected || !Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID}
          onClick={() => removeModal.openModal({
            tenant: selectedTreeNode,
            getData,
            removeIdentity,
            identities: Object.values(selected),
          })}
        >{BUTTON_REMOVE}</Button>
      </>
    )
  }

  const title = useMemo(() => {
    return (<div className='tw-flex tw-gap-2 tw-overflow-hidden'>{LABEL_IDENTITIES_SUBTENANT}<TruncateText addClassName="tw-block">{selectedTreeNode?.display_name}</TruncateText></div>)
  }, [selectedTreeNode?.display_name])

  const customHook = (hooks) => {
    hooks.getToggleAllRowsSelectedProps.push((props, { instance }) => {
      const selected = instance.flatRows.filter(row => {
        return row.original.inherited_from.includes(selectedTreeNode?.id)
      })
      const isAllRowsSelected = instance.rows.filter((row) => row.isSelected)?.length === selected.length
      const checked = ARRAY.hasArray(selected) && instance.rows.filter((row) => row.isSelected)?.length === selected.length
      const indeterminate = Boolean(!isAllRowsSelected && Object.keys(instance.state.selectedRowIds)?.length)
      return [props,
        {
          onChange: () => instance.toggleAllRowsSelected(!isAllRowsSelected),
          style: { cursor: 'pointer' },
          checked,
          title: undefined,
          indeterminate,
        },
      ]
    })
  }

  const useControlledState = useCallback((state, { instance }) => {
    const selectedRowIds = Object.keys(state.selectedRowIds)
      .map((index) => ({ index, user: instance?.data[Number(index)] }))
      .filter(({ index, user }) => Boolean(user) && user.inherited_from?.includes(selectedTreeNode?.id))
      .reduce((map, row) => {
        map[row.index] = true
        return map
      }, {})
    return Object.assign({}, state, { selectedRowIds })
  }, [selectedTreeNode?.id])

  return (
    <>
      {rolesEnabled && <FiltersBar {...{ setFilters, apps, roles }} />}
      <div className="tw-relative tw-w-full tw-h-full">
        <V5TableWrapper id="identities-container" className="tw-flex tw-flex-grow tw-flex-col tw-p-3 tw-absolute tw-inset-0">
          <TableProvider {...{ columns }}
            data={updateCheckboxes(rows)}
            enableColumnSort
            manualSortBy
            enablePagination
            enableRowSelect
            enableResizeColumns
            enableColumnOrder
            isLoading={loading}
            onSortChange={onSortChange}
            onSelectedRowsChange={() => {
              updateCheckboxes(rows)
            }}
            useControlledState={useControlledState}
            overrideHooks={[customHook]}
          >
            <TableHeader title={title} subtitle={LABEL_HEADER_SUBTITLE}>
              <ConnectedTableFilter placeholder={PLACEHOLDER_SEARCH}/>
              <RightControls/>
            </TableHeader>
            <ConnectedTableLayout
              sidecar={<div/>}
              overflow
              paginator={<ConnectedPaginator/>}
            >
              <ConnectedTable
                emptyStateProps={{
                  heading: canListPolicies ? 'No Results Available' : 'You don\'t have access to view this page.',
                }}
                appearance='primary'
                shouldHighlightRow={shouldHighlightRow}
              />
            </ConnectedTableLayout>
          </TableProvider>
        </V5TableWrapper>
      </div>
    </>
  )
}

IdentitiesTable.propTypes = {
  selectedTreeNode: PropTypes.object
}
export default IdentitiesTable
